import { createSlice } from '@reduxjs/toolkit'
import {
  IAddInsuranceColumnPayloadType,
  IConfigDetails,
  IInitInsuranceCriteriaPayloadType,
  IInsertRowPayloadType,
  IInsertRowsPayloadType,
  IPopulateConfigDetailsPayloadType,
  IPupulateTableIdLookupPayloadType,
  IRuleConfigState,
  ISetInsuranceColumnsPayloadType,
  ISetInsuranceCoverageModePayloadType,
  ITableRecordType,
  ISetRulesPayloadType,
  IUpdateCellPayloadType,
  IUpdateRuleMetaPayloadType,
} from '../types/ruleConfig.types'
import { buildRuleCompositeKey } from '../utils/functions'

const initialState: IRuleConfigState = {
  ruleLoadingState: [],
  ruleConfig: {},
  configDetails: {
    selected_product: '',
    selected_program: '',
    selected_criteria: '',
    selected_service: '',
    selected_sub_service: '',
  },
}

const ruleConfigSlice = createSlice({
  name: 'ruleConfig',
  initialState,
  reducers: {
    setRules: (state, action: ISetRulesPayloadType) => {
      const { rules } = action.payload
      const nextConfig: IRuleConfigState['ruleConfig'] = {}
      const seenCompositeKeys = new Set<string>()

      rules?.forEach((rule) => {
        if (!rule?.name || !rule?.criteria || !rule?.criteria_category) {
          return
        }

        const compositeKey = buildRuleCompositeKey(rule)
        if (seenCompositeKeys.has(compositeKey)) {
          return
        }
        seenCompositeKeys.add(compositeKey)

        nextConfig[compositeKey] = {
          rows: {},
          row_order: [],
          rule_meta: {
            record_status: rule.record_status,
            name: rule.name,
            rule_id: rule.id.toString(),
            rule_label: rule.label,
            criteria: rule.criteria,
            criteria_category: rule.criteria_category,
            composite_key: compositeKey,
            config_id: rule.config_id,
            api_fetched: false,
          },
        }
      })

      state.ruleConfig = nextConfig
    },
    resetRules: (state) => {
      state.ruleConfig = {}
    },
    insertRows: (state, action: IInsertRowsPayloadType) => {
      const { rule_id, rows } = action.payload
      if (!state?.ruleConfig?.[rule_id]) {
        state.ruleConfig[rule_id] = {
          rows: {},
          row_order: [],
          rule_meta: {
            record_status: null,
            name: '',
          },
        }
      }

      const ruleState = state.ruleConfig[rule_id]
      const existingOrder = ruleState.row_order
      const hasOnlyLocalRows =
        existingOrder.length > 0 && existingOrder.every((id) => Number(id) < 0)

      if (hasOnlyLocalRows) {
        const localRowIds = [...existingOrder]

        ruleState.row_order = []
        rows.forEach((row) => {
          const row_id = row.id
          ruleState.rows[row_id] = row
          ruleState.row_order.push(row_id)
        })
        localRowIds.forEach((id) => ruleState.row_order.push(id))
        return
      }

      rows.forEach((row) => {
        const row_id = row.id
        ruleState.rows[row_id] = row
        ruleState.row_order.push(row_id)
      })
    },
    updateCell: (state, action: IUpdateCellPayloadType) => {
      const { rule_id, row_id, column_id, content } = action.payload
      if (state?.ruleConfig?.[rule_id]) {
        const row = state.ruleConfig[rule_id].rows[row_id]
        if (row) {
          row[column_id] = content
        }
      }
    },
    updateRuleMeta: (state, action: IUpdateRuleMetaPayloadType) => {
      const { rule_id, record_status, api_fetched } = action.payload
      const meta = state.ruleConfig?.[rule_id]?.rule_meta
      if (!meta) return

      if (record_status !== undefined) {
        meta.record_status = record_status
        if (record_status === 'INACTIVE') meta.api_fetched = false
      }
      if (api_fetched !== undefined) meta.api_fetched = api_fetched
    },
    populateTableIdLookup: (
      state,
      action: IPupulateTableIdLookupPayloadType
    ) => {
      state.ruleConfig[action.payload.rule_id].table_id_lookup =
        action.payload.data
    },
    resetRuleConfig: (state) => {
      state.ruleConfig = {}
      state.configDetails = {
        selected_criteria: '',
        selected_product: '',
        selected_program: '',
        selected_service: '',
        selected_sub_service: '',
      }
    },
    insertRow: (state, action: IInsertRowPayloadType) => {
      const { rule_id, row } = action.payload
      if (!state?.ruleConfig?.[rule_id]) {
        state.ruleConfig[rule_id] = {
          rows: {},
          row_order: [],
          rule_meta: {
            record_status: null,
            name: '',
          },
        }
      }
      state.ruleConfig[rule_id].rows[row.id] = row
      state.ruleConfig[rule_id].row_order.push(row.id)
    },
    updateRow: (state, action: IInsertRowPayloadType) => {
      const { rule_id, row } = action.payload
      if (!state?.ruleConfig?.[rule_id]) {
        state.ruleConfig[rule_id] = {
          rows: {},
          row_order: [],
          rule_meta: {
            record_status: null,
            name: '',
          },
        }
      }
      state.ruleConfig[rule_id].rows = {}
      state.ruleConfig[rule_id].rows[row.id] = row
      state.ruleConfig[rule_id].row_order = [row.id]
    },
    populateConfigDetails: (
      state,
      action: IPopulateConfigDetailsPayloadType
    ) => {
      state.configDetails[action.payload.name as keyof IConfigDetails] =
        action.payload.value
    },
    handleRuleConfigLoading: (state, action: { payload: string }) => {
      if (state?.ruleLoadingState?.includes(action.payload)) {
        state.ruleLoadingState = state.ruleLoadingState.filter(
          (item) => item !== action.payload
        )
      } else {
        state.ruleLoadingState.push(action.payload)
      }
    },
    setInsuranceCoverageMode: (
      state,
      action: ISetInsuranceCoverageModePayloadType
    ) => {
      const { rule_id, mode } = action.payload
      if (state.ruleConfig[rule_id]) {
        state.ruleConfig[rule_id].coverage_mode = mode
      }
    },
    addInsuranceColumn: (state, action: IAddInsuranceColumnPayloadType) => {
      const { rule_id, column, rowPrefix, defaultStatus } = action.payload
      if (!state.ruleConfig[rule_id]) return
      const rule = state.ruleConfig[rule_id]
      if (!rule.insurance_columns) {
        rule.insurance_columns = []
      }
      rule.insurance_columns.push(column)
      rule.row_order
        .filter((id) => id.startsWith(rowPrefix))
        .forEach((rowId) => {
          if (rule.rows[rowId]) {
            rule.rows[rowId][column.id] = {
              id: null,
              value: defaultStatus,
            } as unknown as ITableRecordType
          }
        })
    },
    setInsuranceColumns: (state, action: ISetInsuranceColumnsPayloadType) => {
      const { rule_id, columns } = action.payload
      if (!state.ruleConfig[rule_id]) return
      state.ruleConfig[rule_id].insurance_columns = columns
    },
    initInsuranceCriteria: (
      state,
      action: IInitInsuranceCriteriaPayloadType
    ) => {
      const { rule_id, coverage_mode, insurance_columns, rows, row_order } =
        action.payload
      const rule = state.ruleConfig[rule_id]
      if (!rule || rule.rule_meta?.api_fetched) return

      rule.coverage_mode = coverage_mode
      rule.insurance_columns = insurance_columns
      rule.rows = rows
      rule.row_order = row_order
      if (rule.rule_meta) {
        rule.rule_meta.api_fetched = true
      }
    },
  },
})

export const {
  setRules,
  insertRows,
  updateCell,
  updateRuleMeta,
  resetRuleConfig,
  populateTableIdLookup,
  populateConfigDetails,
  insertRow,
  updateRow,
  resetRules,
  handleRuleConfigLoading,
  setInsuranceCoverageMode,
  addInsuranceColumn,
  setInsuranceColumns,
  initInsuranceCriteria,
} = ruleConfigSlice.actions
export default ruleConfigSlice.reducer
